import { useEffect, useState } from 'react';

/**
 * Tracks which of the given section ids is currently the "active" one while the
 * user scrolls, plus whether the tracked region is on screen at all.
 *
 * The active step is the last section whose top has crossed a reference line
 * near the upper third of the viewport, so the marker updates as each section
 * takes over the reading area.
 */
export function useStepProgress(ids: string[]) {
  const [active, setActive] = useState(0);
  const [inRange, setInRange] = useState(false);

  useEffect(() => {
    let frame = 0;
    let retry = 0;

    const compute = () => {
      frame = 0;
      const els = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => Boolean(el));

      if (els.length === 0) {
        retry = window.setTimeout(compute, 250);
        return;
      }

      const vh = window.innerHeight;
      const line = vh * 0.42;

      let next = 0;
      els.forEach((el, index) => {
        if (el.getBoundingClientRect().top <= line) next = index;
      });

      const firstTop = els[0].getBoundingClientRect().top;
      const lastBottom = els[els.length - 1].getBoundingClientRect().bottom;

      setActive(next);
      setInRange(firstTop <= vh * 0.66 && lastBottom >= vh * 0.34);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (retry) window.clearTimeout(retry);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  return { active, inRange };
}